import { alpha } from "@mui-wrapper";
import { blue, grey, HEX_ALPHA } from "@flat-ui";
import { DEFAULT_FONT_FAMILY } from "@styles/common-styles";
import { getChartLabel, getChartLegendExisting, getChartLegendNew } from "@utils/analytics-utils";
/** Returns the Chart JS options */
export function getChartJsOptions({
  data,
  palette,
  aspectRatio,
  shouldShowTitle = true
}) {
  /* eslint-disable @typescript-eslint/naming-convention -- naming given by external package */

  return {
    responsive: true,
    aspectRatio,
    maintainAspectRatio: true,
    interaction: {
      // Required in order to get the `total count` value in the tooltip
      intersect: false,
      mode: "index"
    },
    plugins: {
      // Title
      title: {
        display: shouldShowTitle,
        text: data.title,
        align: "start",
        font: {
          family: DEFAULT_FONT_FAMILY,
          size: 16,
          weight: 600
        },
        color: palette.text.primary
      },
      // Datasets legends (new ..., existing...)
      legend: {
        labels: {
          font: {
            family: DEFAULT_FONT_FAMILY,
            size: 12
          },
          color: palette.text.secondary,
          usePointStyle: true,
          boxWidth: 8,
          boxHeight: 8
        },
        align: "end",
        // Reversing the legends order will place the "new" legend first
        reverse: true
      },
      // Tooltip
      tooltip: {
        // Container. Deliberately dark in both colour schemes, matching the flat-ui tooltip, so
        // the literals below are the pair that makes it legible rather than light-mode leftovers.
        backgroundColor: grey[950],
        padding: 12,
        cornerRadius: 4,
        // Total count
        titleColor: grey[100],
        titleFont: {
          family: DEFAULT_FONT_FAMILY,
          size: 12
        },
        titleMarginBottom: 8,
        // Datasets
        bodyColor: grey[100],
        bodyFont: {
          family: DEFAULT_FONT_FAMILY,
          size: 12
        },
        bodySpacing: 6,
        // Label (date)
        footerColor: grey[100],
        footerFont: {
          family: DEFAULT_FONT_FAMILY,
          size: 12
        },
        footerMarginTop: 20,
        // Datasets icon
        usePointStyle: true,
        boxWidth: 10,
        boxHeight: 10,
        boxPadding: 12,
        callbacks: {
          // Show the total count in the title section
          title: tooltipItems => {
            let sum = 0;
            tooltipItems.forEach(item => {
              sum += item.parsed.y ?? 0;
            });
            return `Total: ${sum.toString()}`;
          },
          // Show the label (date) in the footer section
          footer: tooltipItems => {
            let label = "";
            tooltipItems.forEach(item => {
              // Format label from "Mmm'yy" to "Mmm yyyy"
              const monthAndYear = item.label.split("'");
              const month = monthAndYear[0];
              const year = monthAndYear[1];
              const formattedYear = `20${year}`;
              label = `${month} ${formattedYear}`;
            });
            return label;
          }
        }
      }
    },
    // Axes
    scales: {
      x: {
        stacked: true,
        grid: {
          color: palette.divider,
          // Setting grid line width to zero will hide the grid lines but still show the ticks
          lineWidth: 0,
          tickWidth: 1,
          tickLength: 5
        },
        border: {
          color: palette.text.primary,
          width: 2
        },
        ticks: {
          major: {
            enabled: true
          },
          color(ctx) {
            let tickColor = palette.text.secondary;

            // Get hovered bar (active elements)
            const elements = ctx.chart.getActiveElements();
            // Get tick index
            const tickIndex = ctx.index;
            // If active bar index matches the tick index change font color
            if (elements.length && tickIndex === elements[0].index) {
              tickColor = blue[500];
            }
            return tickColor;
          },
          font: {
            family: DEFAULT_FONT_FAMILY,
            size: 10
          },
          // Takes care of formatting data labels to be visible in 2 rows
          callback: (_, index) => {
            return getChartLabel({
              labels: data.labels,
              index
            });
          }
        }
      },
      y: {
        stacked: true,
        grid: {
          color: palette.divider,
          tickLength: 13
        },
        border: {
          display: false
        },
        ticks: {
          maxTicksLimit: 4,
          color: palette.text.secondary,
          font: {
            family: DEFAULT_FONT_FAMILY,
            size: 10
          },
          format: {
            // Automatically reduce large values (millions, thousands) to a compact notation (M, m)
            notation: "compact",
            // Add commas to large numbers
            useGrouping: true
          },
          // Setting precision to zero will only generate tick steps with integers
          precision: 0
        }
      }
    },
    // Bars
    datasets: {
      bar: {
        // Set 1px gap between bars
        barPercentage: 0.99,
        categoryPercentage: 1
      }
    },
    onHover: (_, elements, chart) => {
      // Update chart when hovering over bars so the color of the label text changes
      if (elements.length) {
        chart.update();
      }
    }
  };

  /* eslint-enable @typescript-eslint/naming-convention */
}
/** Returns the Chart JS data */
export function getChartJsData({
  data
}) {
  return {
    labels: data.labels,
    datasets: [{
      label: getChartLegendExisting(data.name),
      data: data.datasets.existing,
      backgroundColor: alpha(blue[400], HEX_ALPHA.alphaCC),
      hoverBackgroundColor: alpha(blue[400], HEX_ALPHA.alpha99)
    }, {
      label: getChartLegendNew(data.name),
      data: data.datasets.new,
      backgroundColor: alpha(blue[400], HEX_ALPHA.alpha66),
      hoverBackgroundColor: alpha(blue[400], HEX_ALPHA.alpha4D)
    }]
  };
}